import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, DestroyRef, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { CurrentIdTokenService } from '@padspin/ui-auth';
import { PushNotificationService } from '../push-notification-service/push-notification.service';

const NOTIFICATION_PERMISSIONS_PATH =
  '/account-manager-onboarding/notification-permissions';

/**
 * Eagerly-initialized service that enforces notification permission for
 * account managers. If an authenticated user has account_manager or
 * pending_account_manager claims and Notification.permission is not 'granted',
 * they are redirected to the notification permissions page. Admins are never
 * redirected, but still get their FCM token registered.
 *
 * Also ensures the FCM token and foreground listener are registered on startup
 * when permission is already granted.
 */
@Injectable({ providedIn: 'root' })
export class NotificationPermissionEnforcerService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly currentIdTokenService = inject(CurrentIdTokenService);
  private readonly pushService = inject(PushNotificationService);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    const navigationEnd$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
    );

    // Redirect account managers to notification permissions page if not granted
    navigationEnd$
      .pipe(
        filter((e) => {
          const url = (e as NavigationEnd).urlAfterRedirects;
          return (
            !url.startsWith(NOTIFICATION_PERMISSIONS_PATH) &&
            !url.startsWith('/dashboard/admin') &&
            !url.startsWith('/admin')
          );
        }),
        filter(
          () =>
            typeof Notification !== 'undefined' &&
            Notification.permission !== 'granted',
        ),
        filter(() => this.isAccountManager() && !this.isAdmin()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.router.navigate([NOTIFICATION_PERMISSIONS_PATH], {
          queryParams: { returnUrl: (e as NavigationEnd).urlAfterRedirects },
        });
      });

    // On first navigation, ensure FCM token is registered if permission is already granted
    navigationEnd$
      .pipe(
        take(1),
        filter(
          () =>
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted',
        ),
        filter(() => this.isAccountManager()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.pushService.ensureRegistered();
      });
  }

  private isAdmin(): boolean {
    return (
      this.currentIdTokenService.currentUserIdToken()?.claims?.['admin'] ===
      true
    );
  }

  private isAccountManager(): boolean {
    const claims = this.currentIdTokenService.currentUserIdToken()?.claims;
    return (
      claims?.['account_manager'] === true ||
      claims?.['pending_account_manager'] === true
    );
  }
}
